import { MAPTERHORN, TerrainEncoding, TerrainTileSource } from './terrain_encoding';

/**
 * The DEM source, served from the admin `app_settings` table so it can be changed without a deploy.
 *
 * Rails renders the row's value into `<meta name="elevation-api-config">`; an absent or unusable one
 * leaves the compiled Mapterhorn default in place. Unlike iOS and Android there is no fetch, no stored
 * copy and no foreground refresh — the server already validated this and put it in the page.
 */
export interface ElevationApiConfigJson {
  url?: unknown;
  encoding?: unknown;
  tileSize?: unknown;
  maxZoom?: unknown;
}

const DEFAULT_TILE_SIZE = 512;
const DEFAULT_MAX_ZOOM = 12;
const TILE_SIZE_RANGE = { min: 64, max: 2048 };
const MAX_ZOOM_RANGE = { min: 0, max: 22 };

/** The Mapbox style-spec `raster-dem` names, so a value copied from a style JSON works unchanged. */
const ENCODINGS: Record<string, TerrainEncoding> = {
  terrarium: 'terrarium',
  mapbox: 'mapboxTerrainRGB'
};

/**
 * Derived from the URL with the query string dropped, so it stays stable across a rotated credential —
 * matching how iOS and Android namespace their tile caches.
 */
export function slugFor(url: string): string {
  const slug = url
    .split('?')[0]
    .replace(/[^a-zA-Z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

  return slug || 'source';
}

function isInteger(value: unknown, range: { min: number; max: number }): value is number {
  return typeof value === 'number' && Number.isInteger(value) && value >= range.min && value <= range.max;
}

/**
 * Null for anything unusable. Validated here as well as in Rails because the meta tag is the only
 * thing between an admin typo and a readout in the hundreds of thousands of metres — decoding a
 * terrarium tile with the Mapbox formula throws nothing.
 */
export function terrainSourceFromJson(json: ElevationApiConfigJson | null): TerrainTileSource | null {
  if (!json) return null;

  const { url, encoding } = json;
  if (typeof url !== 'string') return null;
  if (!['{z}', '{x}', '{y}'].every((token) => url.includes(token))) return null;

  const resolvedEncoding = typeof encoding === 'string' ? ENCODINGS[encoding] : undefined;
  if (!resolvedEncoding) return null;

  const tileSize = json.tileSize === undefined ? DEFAULT_TILE_SIZE : json.tileSize;
  const maxZoom = json.maxZoom === undefined ? DEFAULT_MAX_ZOOM : json.maxZoom;
  if (!isInteger(tileSize, TILE_SIZE_RANGE)) return null;
  if (!isInteger(maxZoom, MAX_ZOOM_RANGE)) return null;

  return {
    slug: slugFor(url),
    tileSize,
    maximumZoom: maxZoom,
    encoding: resolvedEncoding,
    attributionUrl: attributionFor(url),
    tileUrl: (z, x, y) =>
      url.replace('{z}', String(z)).replace('{x}', String(x)).replace('{y}', String(y))
  };
}

/** A configured provider still has to be credited, so fall back to its own origin. */
function attributionFor(url: string): string {
  if (url.includes('tiles.mapterhorn.com')) return MAPTERHORN.attributionUrl;
  if (url.includes('api.mapbox.com')) return 'https://www.mapbox.com/about/maps/';

  try {
    return new URL(url.split('?')[0]).origin;
  } catch {
    return url;
  }
}

export function parseElevationApiConfig(raw: string | null): TerrainTileSource | null {
  if (!raw || !raw.trim()) return null;

  try {
    return terrainSourceFromJson(JSON.parse(raw) as ElevationApiConfigJson);
  } catch {
    return null;
  }
}

let resolved: TerrainTileSource | undefined;

/**
 * Read once per page. The meta tag cannot change without a navigation, and every map on the page
 * shares one answer.
 */
export function configuredTerrainSource(): TerrainTileSource {
  if (resolved === undefined) {
    const meta = document.querySelector('meta[name="elevation-api-config"]');
    resolved = parseElevationApiConfig(meta?.getAttribute('content') ?? null) ?? MAPTERHORN;
  }

  return resolved;
}

/** Test seam — the module-level cache would otherwise leak one spec's meta tag into the next. */
export function resetConfiguredTerrainSource(): void {
  resolved = undefined;
}
